import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalMallOutlinedIcon from "@mui/icons-material/LocalMallOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import SpaIcon from "@mui/icons-material/Spa";
import SupervisedUserCircleOutlinedIcon from "@mui/icons-material/SupervisedUserCircleOutlined";
import { useTranslation } from "react-i18next";
import SidebarLayout from "../components/sidebarLayout";
import { getAllCategorie } from "../APIs/CategoriesAPI";
import {
  addSpaOperatorQualification,
  createSpaTreatmentAddon,
  createSpaCabin,
  createSpaCabinBlock,
  createSpaOperator,
  createSpaOperatorScheduleEntry,
  createSpaEquipment,
  createSpaInventoryProduct,
  createSpaTreatment,
  dateTimeWithLocalOffset,
  deactivateSpaCabin,
  deactivateSpaOperator,
  deactivateSpaEquipment,
  deactivateSpaTreatmentAddon,
  getSpaCabinBlocks,
  getSpaCabinBoard,
  getSpaCabins,
  getSpaEquipment,
  getSpaEquipmentItem,
  getSpaInventoryProducts,
  getSpaOperatorQualifications,
  getSpaOperators,
  getSpaOperatorSchedule,
  getSpaTreatmentAddons,
  getSpaTreatments,
  removeSpaOperatorQualification,
  removeSpaOperatorScheduleEntry,
  removeSpaCabinBlock,
  updateSpaCabin,
  updateSpaEquipment,
  updateSpaInventoryProduct,
  updateSpaOperator,
  updateSpaTreatment,
  updateSpaTreatmentAddon,
} from "../APIs/SpaAPI";
import { getAllProdotti } from "../APIs/ProductsAPI";
import { usePermissions } from "../contexts/PermissionsContext";
import { useSnackbar } from "../contexts/SnackBarContext";
import { SpaEquipmentFields, SpaTreatmentEquipment, validEquipmentPlacements } from '../components/SpaEquipmentSetup';
import SpaEquipmentBlocks from '../components/SpaEquipmentBlocks';

const tabs = [
  { key: "treatments", icon: <SpaIcon fontSize="small" />, label: "Treatments", singular: "Treatment" },
  { key: "operators", icon: <SupervisedUserCircleOutlinedIcon fontSize="small" />, label: "Operators", singular: "Operator" },
  { key: "cabins", icon: <MeetingRoomOutlinedIcon fontSize="small" />, label: "Cabins", singular: "Cabin" },
  { key: "equipment", icon: <Inventory2OutlinedIcon fontSize="small" />, label: "Equipment", singular: "Equipment" },
  { key: "products", icon: <LocalMallOutlinedIcon fontSize="small" />, label: "Products", singular: "Product" },
];

const operatorSkillLabels = (operator = {}, qualifications = []) => Array.from(new Set([
  ...(Array.isArray(operator.specialties) ? operator.specialties : []),
  ...qualifications.map((qualification) => (
    qualification?.treatment?.name || qualification?.skill || ""
  )),
].map((value) => String(value || "").trim()).filter(Boolean)));

const loadOperatorsWithQualifications = async (token, tenant) => {
  const operators = await getSpaOperators(token, tenant);
  return Promise.all(operators.map(async (operator) => {
    const qualifications = await getSpaOperatorQualifications(operator.id, token, tenant).catch(() => []);
    return {
      ...operator,
      qualifications,
      skills: operatorSkillLabels(operator, qualifications),
    };
  }));
};

const emptyByType = {
  treatments: {
    name: "",
    categoryId: "",
    subcategoryId: "",
    durationMinutes: 50,
    preparationMinutes: 0,
    cleanupMinutes: 0,
    price: 0,
    vatRate: 22,
    active: true,
  },
  operators: {
    name: "",
    surname: "",
    email: "",
    phone: "",
    languages: "",
    specialties: "",
    engagementReminderMinutes: 60,
    active: true,
  },
  cabins: {
    name: "",
    cabinType: "MASSAGE",
    capacity: 1,
    characteristics: "",
    active: true,
    blockType: "MAINTENANCE",
    blockDate: "",
    blockStartTime: "09:00",
    blockEndTime: "18:00",
    blockNote: "",
  },
  equipment: {
    name: "",
    description: "",
    quantity: 1,
    active: true,
    placements: [],
  },
  products: {
    id: null,
    productId: null,
    name: "",
    unit: "PIECE",
    retailCategory: "OTHER",
    unitCost: 0,
    sellingPrice: 0,
  },
};

function normalizeDraft(type, item = null) {
  const base = { ...emptyByType[type], ...(item || {}) };
  if (Array.isArray(base.skills)) {
    base.skills = base.skills.join(", ");
  }
  if (Array.isArray(base.languages)) {
    base.languages = base.languages.join(", ");
  }
  if (Array.isArray(base.specialties)) {
    base.specialties = base.specialties.join(", ");
  }
  if (Array.isArray(base.characteristics)) {
    base.characteristics = base.characteristics.join(", ");
  }
  return base;
}

const commaList = (value) => String(value || "")
  .split(",")
  .map((item) => item.trim())
  .filter(Boolean);

const stripRepeatedSurname = (name, surname) => {
  let next = String(name || "").trim();
  const suffix = String(surname || "").trim();
  if (!suffix) return next;
  const pattern = new RegExp(`\\s+${suffix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`, "i");
  while (pattern.test(next)) {
    next = next.replace(pattern, "").trim();
  }
  return next || String(name || "").trim();
};

function prepareSavedItem(type, draft) {
  const item = { ...draft };
  if (type === "treatments") {
    item.durationMinutes = Number(item.durationMinutes || 0);
    item.preparationMinutes = Number(item.preparationMinutes || 0);
    item.cleanupMinutes = Number(item.cleanupMinutes || 0);
    item.price = Number(item.price || 0);
    item.vatRate = Number(item.vatRate || 0);
    item.active = Boolean(item.active);
  }
  if (type === "equipment") {
    item.quantity = Number(item.quantity || 0);
  }
  if (type === "products") {
    item.unitCost = Number(item.unitCost || 0);
    item.sellingPrice = Number(item.sellingPrice || 0);
  }
  if (type === "operators") {
    item.languages = commaList(item.languages);
    item.specialties = commaList(item.specialties || item.skills);
    item.engagementReminderMinutes = Math.min(10080, Math.max(5, Number(item.engagementReminderMinutes || 60)));
    item.active = item.active !== false;
  }
  if (type === "cabins") {
    item.capacity = Number(item.capacity || 1);
    item.characteristics = commaList(item.characteristics);
    item.active = item.active !== false;
  }
  return item;
}

const localDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const todayKey = () => localDateKey();
const scheduleRange = () => ({
  from: dateTimeWithLocalOffset(todayKey(), "00:00"),
  to: dateTimeWithLocalOffset(todayKey(), "23:59"),
});

const currentCabinStatus = (cabin, now = new Date()) => {
  if (cabin.active === false) return "OUT_OF_SERVICE";
  const instant = new Date(now).getTime();
  const interval = (cabin.statusIntervals || []).find((item) => {
    const start = new Date(item.startAt).getTime();
    const end = new Date(item.endAt).getTime();
    return start <= instant && instant < end;
  });
  if (["MAINTENANCE", "OUT_OF_SERVICE", "CLEANING"].includes(interval?.status)) return interval.status;
  if (interval?.status === "OCCUPIED") return "OCCUPIED";
  const hasUpcomingAppointment = (cabin.appointments || []).some((appointment) => {
    const commercialStatus = String(appointment?.commercialStatus || "").toUpperCase();
    if (["CANCELLED", "NO_SHOW", "COMPLETED"].includes(commercialStatus)) return false;
    return new Date(appointment.endAt).getTime() > instant;
  });
  if (hasUpcomingAppointment) return "RESERVED";
  return interval?.status || "AVAILABLE";
};

const cabinBlockTypeOptions = ["CLEANING", "MAINTENANCE", "OUT_OF_SERVICE"];
const treatmentAddOnTypes = ["EXTRA_SERVICE", "TREATMENT_UPGRADE", "ADDITIONAL_SERVICE", "PRODUCT"];
const isAreaResource = (resource) => /^area\s*\d+$/i.test(String(resource?.name || "").trim());

const emptyTreatmentAddOnDraft = () => ({
  id: null,
  productId: "",
  type: "EXTRA_SERVICE",
  priceDelta: 0,
  durationDeltaMinutes: 0,
  active: true,
  operatorCount: 1,
  compatibleCabinIds: [],
  requiredOperatorSkills: "",
  equipment: [],
  affectedAreaIds: [],
  consumables: [],
});

const normalizeTreatmentAddOnDraft = (addOn = null) => {
  if (!addOn) return emptyTreatmentAddOnDraft();
  const resources = addOn.resourceChanges || addOn.requiredResources || {};
  return {
    ...emptyTreatmentAddOnDraft(),
    ...addOn,
    productId: String(addOn.product?.id || addOn.productId || ""),
    priceDelta: Number(addOn.priceDelta ?? addOn.priceAdjustment ?? 0),
    durationDeltaMinutes: Number(addOn.durationDeltaMinutes ?? addOn.durationAdjustmentMinutes ?? 0),
    operatorCount: Number(resources.operatorCount || 1),
    compatibleCabinIds: (resources.compatibleCabinIds || []).map(String),
    requiredOperatorSkills: (resources.requiredOperatorSkills || []).join(", "),
    equipment: Array.isArray(resources.equipment) ? resources.equipment : [],
    affectedAreaIds: Array.isArray(resources.affectedAreaIds) ? resources.affectedAreaIds : [],
    consumables: Array.isArray(resources.consumables) ? resources.consumables : [],
  };
};

const sortTreatments = (items = []) => [...items].sort((a, b) => (
  String(a.name || "").localeCompare(String(b.name || ""), undefined, { numeric: true, sensitivity: "base" })
));

const sortOperators = (items = []) => [...items].sort((a, b) => (
  String(a.displayName || a.name || "").localeCompare(String(b.displayName || b.name || ""), undefined, { numeric: true, sensitivity: "base" })
));

function StatusChip({ value }) {
  const { t } = useTranslation();
  const normalized = String(value || "").toLowerCase();
  const color = ["available", "active"].includes(normalized) || value === true
    ? "success"
    : ["occupied", "cleaning", "preparation", "reserved"].includes(normalized)
      ? "warning"
      : ["maintenance", "out_of_service"].includes(normalized)
        ? "error"
        : "default";
  const label = value === true
    ? t("spa.catalog.statuses.active")
    : value === false
      ? t("spa.catalog.statuses.inactive")
      : t(`spa.catalog.statuses.${normalized}`, { defaultValue: String(value || "-").replace(/_/g, " ") });
  return <Chip size="small" color={color} variant="outlined" label={label} />;
}

function SpaCatalogPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { can } = usePermissions();
  const { showSnackbar } = useSnackbar();
  const isDark = theme.palette.mode === "dark";
  const tenantType = String(localStorage.getItem("tenantType") || "").toUpperCase();
  const token = localStorage.getItem("accessToken");
  const tenant = localStorage.getItem("tenantId");
  const canViewCatalog = can("spa-catalogs:view");
  const canViewResources = can("spa-resources:view");
  const canViewFinance = can("spa-finance:view");
  const canViewBoard = canViewResources && can("spa-appointments:view");
  const canViewCategories = can("categories:view");
  const canManageResources = canViewResources && can("spa-resources:manage");
  const canManageCatalog = canViewCatalog && can("spa-catalogs:manage");
  const canViewProducts = can("products:view");
  const [activeTab, setActiveTab] = useState("treatments");
  const canManageActiveTab = activeTab === "treatments" ? canManageCatalog : canManageResources;
  const [catalog, setCatalog] = useState({
    treatments: [],
    operators: [],
    cabins: [],
    equipment: [],
    products: [],
  });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState(() => normalizeDraft("treatments"));
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [equipmentBusy, setEquipmentBusy] = useState(false);
  const [equipmentLoaded, setEquipmentLoaded] = useState(false);
  const [equipmentError, setEquipmentError] = useState('');
  const [inventoryProductsLoaded, setInventoryProductsLoaded] = useState(false);
  const [inventoryProductsError, setInventoryProductsError] = useState('');
  const [equipmentDetail, setEquipmentDetail] = useState({ ready: false, error: '' });
  const [equipmentDetailReload, setEquipmentDetailReload] = useState(0);
  const [confirmEquipmentDeactivate, setConfirmEquipmentDeactivate] = useState(false);
  const equipmentPending = activeTab === 'equipment' && draft.id && !equipmentDetail.ready;
  const [catalogReload, setCatalogReload] = useState(0);
  const saveInFlight = useRef(false);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [treatmentAddOns, setTreatmentAddOns] = useState([]);
  const [treatmentAddOnsLoading, setTreatmentAddOnsLoading] = useState(false);
  const [treatmentAddOnDraft, setTreatmentAddOnDraft] = useState(emptyTreatmentAddOnDraft);
  const [treatmentAddOnSaving, setTreatmentAddOnSaving] = useState(false);
  const addOnReadOnly = !canManageCatalog || saving || equipmentBusy || treatmentAddOnSaving;
  const [equipmentRequirementDraft, setEquipmentRequirementDraft] = useState({ equipmentId: "", quantity: 1 });
  const [operatorDetails, setOperatorDetails] = useState({ qualifications: [], weekly: [], blocks: [] });
  const [cabinBlocks, setCabinBlocks] = useState([]);
  const [operatorQualificationDraft, setOperatorQualificationDraft] = useState({ treatmentProductId: "", skill: "" });
  const [operatorScheduleDraft, setOperatorScheduleDraft] = useState({
    type: "SHIFT",
    dayOfWeek: new Date().getDay(),
    startTime: "09:00",
    endTime: "18:00",
    note: "",
  });

  useEffect(() => {
    if (!dialogOpen || activeTab !== 'equipment' || !draft.id || !canViewResources) return undefined;
    const controller = new AbortController();
    setEquipmentDetail({ ready: false, error: '' });
    getSpaEquipmentItem(draft.id, token, tenant, { signal: controller.signal }).then((item) => {
      if (controller.signal.aborted) return;
      setDraft(normalizeDraft('equipment', item));
      setCatalog((current) => ({ ...current, equipment: current.equipment.map((entry) => entry.id === item.id ? item : entry) }));
      setEquipmentDetail({ ready: true, error: '' });
    }).catch((error) => {
      if (!controller.signal.aborted) setEquipmentDetail({ ready: false, error: error.message });
    });
    return () => controller.abort();
  }, [dialogOpen, activeTab, draft.id, canViewResources, token, tenant, equipmentDetailReload]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setEquipmentLoaded(false); setEquipmentError('');
    setInventoryProductsLoaded(false); setInventoryProductsError('');
    Promise.all([
      token && tenant && canViewCatalog ? getSpaTreatments(token, tenant).catch(() => []) : Promise.resolve([]),
      token && tenant && (canViewResources || canViewFinance) ? (canViewResources ? loadOperatorsWithQualifications(token, tenant) : getSpaOperators(token, tenant)).catch(() => []) : Promise.resolve([]),
      token && tenant && (canViewResources || canViewFinance) ? getSpaCabins(token, tenant).catch(() => []) : Promise.resolve([]),
      token && tenant && canViewBoard ? getSpaCabinBoard(scheduleRange(), token, tenant).catch(() => ({ cabins: [] })) : Promise.resolve({ cabins: [] }),
      token && tenant && canViewResources ? getSpaEquipment(token, tenant).then((data) => {
        if (!cancelled) setEquipmentLoaded(true);
        return data;
      }).catch((error) => { if (!cancelled) setEquipmentError(error.message); return []; }) : Promise.resolve([]),
      token && tenant && canViewCategories ? getAllCategorie(token, tenant).catch(() => []) : Promise.resolve([]),
      token && tenant && canViewProducts ? getAllProdotti(token, tenant).catch(() => []) : Promise.resolve([]),
      token && tenant && canViewResources ? getSpaInventoryProducts(token, tenant).then((data) => {
        if (!cancelled) setInventoryProductsLoaded(true);
        return data;
      }).catch((error) => { if (!cancelled) setInventoryProductsError(error.message); return []; }) : Promise.resolve([]),
    ])
      .then(([treatmentData, operatorData, cabinData, cabinBoard, equipmentData, categoryData, productData, inventoryProductData]) => {
        if (cancelled) return;
        const boardById = new Map((cabinBoard?.cabins || []).map((cabin) => [Number(cabin.id), cabin]));
        setCatalog({
          treatments: sortTreatments(treatmentData),
          operators: sortOperators(operatorData),
          cabins: (cabinData || []).filter((cabin) => !isAreaResource(cabin)).map((cabin) => {
            const boardCabin = boardById.get(Number(cabin.id));
            return {
              ...cabin,
              ...(boardCabin || {}),
              ...cabin,
              equipment: boardCabin?.equipment || cabin.equipment || [],
              compatibleTreatments: boardCabin?.compatibleTreatments || cabin.compatibleTreatments || [],
              blocks: boardCabin?.blocks || cabin.blocks || [],
              statusIntervals: boardCabin?.statusIntervals || cabin.statusIntervals || [],
              currentStatus: currentCabinStatus(boardCabin || cabin),
            };
          }),
          equipment: equipmentData || [],
          products: inventoryProductData || [],
        });
        setCategories(Array.isArray(categoryData) ? categoryData : []);
        setProducts(Array.isArray(productData) ? productData : []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenant, token, canViewCatalog, canViewResources, canViewFinance, canViewBoard, canViewCategories, canViewProducts, catalogReload]);

  const activeTabConfig = useMemo(
    () => tabs.find((tab) => tab.key === activeTab) || tabs[0],
    [activeTab]
  );
  const activeTabSingular = t(`spa.catalog.tabs.${activeTabConfig.key}.singular`, { defaultValue: activeTabConfig.singular });
  const addOnProductOptions = useMemo(() => {
    const source = treatmentAddOnDraft.type === "TREATMENT_UPGRADE" ? catalog.treatments : products;
    const byId = new Map();
    source.forEach((product) => {
      const id = Number(product.id);
      if (id && id !== Number(draft.id)) byId.set(id, product);
    });
    return [...byId.values()].sort((left, right) => String(left.name || "").localeCompare(String(right.name || "")));
  }, [catalog.treatments, draft.id, products, treatmentAddOnDraft.type]);

  const pageSurface = {
    background: isDark ? "#151515" : "#FFFFFF",
    borderColor: isDark ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.12)",
  };

  const openCreate = () => {
    if (!canManageActiveTab) return;
    setSaveError("");
    setTreatmentAddOns([]);
    setTreatmentAddOnDraft(emptyTreatmentAddOnDraft());
    setEquipmentRequirementDraft({ equipmentId: "", quantity: 1 });
    setOperatorDetails({ qualifications: [], weekly: [], blocks: [] });
    setCabinBlocks([]);
    setOperatorQualificationDraft({ treatmentProductId: "", skill: "" });
    setOperatorScheduleDraft({
      type: "SHIFT",
      dayOfWeek: new Date().getDay(),
      startTime: "09:00",
      endTime: "18:00",
      note: "",
    });
    setDraft({ ...normalizeDraft(activeTab), blockDate: todayKey() });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setSaveError("");
    setEquipmentDetail({ ready: false, error: '' });
    setConfirmEquipmentDeactivate(false);
    setTreatmentAddOns([]);
    setTreatmentAddOnDraft(emptyTreatmentAddOnDraft());
    setEquipmentRequirementDraft({ equipmentId: "", quantity: 1 });
    setCabinBlocks([]);
    setDraft({ ...normalizeDraft(activeTab, item), blockDate: todayKey() });
    setDialogOpen(true);
    if (activeTab === "operators" && canViewResources) {
      loadOperatorDetails(item.id);
    }
    if (activeTab === "cabins" && canViewResources) {
      loadCabinBlocks(item.id);
    }
    if (activeTab === "treatments") {
      loadTreatmentAddOns(item.id);
    }
  };

  async function loadTreatmentAddOns(treatmentId) {
    setTreatmentAddOnsLoading(true);
    try {
      const addOns = await getSpaTreatmentAddons(treatmentId, token, tenant, { includeInactive: true });
      setTreatmentAddOns(addOns || []);
    } catch (error) {
      setSaveError(error?.message || t("spa.catalog.addOnsLoadError", { defaultValue: "Could not load add-ons" }));
    } finally {
      setTreatmentAddOnsLoading(false);
    }
  }

  const loadOperatorDetails = async (operatorId) => {
    setOperatorDetails({ qualifications: [], weekly: [], blocks: [] });
    try {
      const [qualifications, schedule] = await Promise.all([
        getSpaOperatorQualifications(operatorId, token, tenant),
        getSpaOperatorSchedule(operatorId, scheduleRange(), token, tenant),
      ]);
      setOperatorDetails({
        qualifications: qualifications || [],
        weekly: schedule?.weekly || [],
        blocks: schedule?.blocks || [],
      });
      setCatalog((current) => ({
        ...current,
        operators: current.operators.map((operator) => (
          Number(operator.id) === Number(operatorId)
            ? {
              ...operator,
              qualifications: qualifications || [],
              skills: operatorSkillLabels(operator, qualifications || []),
            }
            : operator
        )),
      }));
    } catch (error) {
      setSaveError(error?.message || t("spa.catalog.operatorDetailsError", { defaultValue: "Could not load operator details" }));
    }
  };

  const loadCabinBlocks = async (cabinId) => {
    setCabinBlocks([]);
    try {
      const blocks = await getSpaCabinBlocks(cabinId, scheduleRange(), token, tenant);
      setCabinBlocks(blocks || []);
    } catch (error) {
      setSaveError(error?.message || t("spa.catalog.cabinBlocksError", { defaultValue: "Could not load cabin blocks" }));
    }
  };

  const addEquipmentRequirement = () => {
    if (!canManageCatalog || saving || treatmentAddOnSaving) return;
    const equipmentId = Number(equipmentRequirementDraft.equipmentId);
    const quantity = Math.max(1, Number(equipmentRequirementDraft.quantity || 1));
    if (!equipmentId) return;
    setTreatmentAddOnDraft((current) => ({
      ...current,
      equipment: [
        ...(current.equipment || []).filter((item) => Number(item.equipmentId) !== equipmentId),
        { equipmentId, quantity },
      ],
    }));
    setEquipmentRequirementDraft({ equipmentId: "", quantity: 1 });
  };

  const removeEquipmentRequirement = (equipmentId) => {
    if (!canManageCatalog || saving || treatmentAddOnSaving) return;
    setTreatmentAddOnDraft((current) => ({
      ...current,
      equipment: (current.equipment || []).filter((item) => Number(item.equipmentId) !== Number(equipmentId)),
    }));
  };

  const saveTreatmentAddOn = async () => {
    if (!canManageCatalog || !draft.id || !treatmentAddOnDraft.productId || treatmentAddOnSaving || saving || equipmentBusy) return;
    setTreatmentAddOnSaving(true);
    setSaveError("");
    const payload = {
      type: treatmentAddOnDraft.type,
      priceDelta: Math.max(0, Number(treatmentAddOnDraft.priceDelta || 0)),
      durationDeltaMinutes: Math.max(0, Number(treatmentAddOnDraft.durationDeltaMinutes || 0)),
      active: treatmentAddOnDraft.active !== false,
      resourceChanges: {
        operatorCount: Math.max(1, Number(treatmentAddOnDraft.operatorCount || 1)),
        compatibleCabinIds: (treatmentAddOnDraft.compatibleCabinIds || []).map(Number).filter(Boolean),
        affectedAreaIds: (treatmentAddOnDraft.affectedAreaIds || []).map(Number).filter(Boolean),
        requiredOperatorSkills: commaList(treatmentAddOnDraft.requiredOperatorSkills).map((skill) => skill.toLowerCase()),
        equipment: (treatmentAddOnDraft.equipment || []).map((item) => ({
          equipmentId: Number(item.equipmentId),
          quantity: Math.max(1, Number(item.quantity || 1)),
        })).filter((item) => item.equipmentId),
        consumables: (treatmentAddOnDraft.consumables || []).map((item) => ({
          productId: Number(item.productId),
          quantity: Number(item.quantity || 1),
        })).filter((item) => item.productId && item.quantity > 0),
      },
    };
    try {
      const saved = treatmentAddOnDraft.id
        ? await updateSpaTreatmentAddon(draft.id, treatmentAddOnDraft.id, payload, token, tenant)
        : await createSpaTreatmentAddon(draft.id, {
          productId: Number(treatmentAddOnDraft.productId),
          ...payload,
        }, token, tenant);
      setTreatmentAddOns((current) => (
        treatmentAddOnDraft.id
          ? current.map((item) => (Number(item.id) === Number(saved.id) ? saved : item))
          : [...current, saved]
      ));
      setTreatmentAddOnDraft(emptyTreatmentAddOnDraft());
      setEquipmentRequirementDraft({ equipmentId: "", quantity: 1 });
      showSnackbar(t("spa.catalog.addOnSaved", { defaultValue: "Add-on saved." }), "success");
    } catch (error) {
      setSaveError(error?.message || t("spa.catalog.addOnSaveError", { defaultValue: "Could not save add-on" }));
    } finally {
      setTreatmentAddOnSaving(false);
    }
  };

  const deactivateTreatmentAddOn = async (addOnId) => {
    if (!canManageCatalog || !draft.id || treatmentAddOnSaving || saving || equipmentBusy) return;
    setTreatmentAddOnSaving(true);
    setSaveError("");
    try {
      const saved = await deactivateSpaTreatmentAddon(draft.id, addOnId, token, tenant);
      setTreatmentAddOns((current) => current.map((item) => (
        Number(item.id) === Number(addOnId) ? saved : item
      )));
      if (Number(treatmentAddOnDraft.id) === Number(addOnId)) {
        setTreatmentAddOnDraft(emptyTreatmentAddOnDraft());
      }
      showSnackbar(t("spa.catalog.addOnDeactivated", { defaultValue: "Add-on deactivated." }), "success");
    } catch (error) {
      setSaveError(error?.message || t("spa.catalog.addOnSaveError", { defaultValue: "Could not save add-on" }));
    } finally {
      setTreatmentAddOnSaving(false);
    }
  };

  const saveDraft = async () => {
    if (!canManageActiveTab || saveInFlight.current || equipmentBusy || equipmentPending || treatmentAddOnSaving) return;
    saveInFlight.current = true;
    setSaving(true);
    setSaveError("");
    try {
      const item = prepareSavedItem(activeTab, draft);

      if (activeTab === "treatments") {
        const payload = {
          name: item.name,
          price: item.price,
          active: item.active,
          durationMinutes: item.durationMinutes,
          preparationMinutes: item.preparationMinutes,
          cleanupMinutes: item.cleanupMinutes,
          fiscalRule: { vatRate: item.vatRate },
          categories: item.categoryId
            ? [{ categoryId: Number(item.categoryId), ...(item.subcategoryId ? { subcategoryId: Number(item.subcategoryId) } : {}) }]
            : [],
        };
        const saved = draft.id
          ? await updateSpaTreatment(draft.id, payload, token, tenant)
          : await createSpaTreatment(payload, token, tenant);

        setCatalog((current) => ({
          ...current,
          treatments: sortTreatments(draft.id
            ? current.treatments.map((entry) => (entry.id === draft.id ? saved : entry))
            : [...current.treatments, saved]),
        }));
        if (!draft.id) {
          setDraft(normalizeDraft('treatments', saved));
          showSnackbar(t('spa.equipment.treatmentCreated'), 'success');
        } else setDialogOpen(false);
        return;
      }

      if (activeTab === "operators") {
        const cleanName = stripRepeatedSurname(item.name, item.surname);
        const payload = {
          name: cleanName,
          surname: item.surname,
          email: item.email || null,
          phone: item.phone || null,
          languages: item.languages,
          specialties: item.specialties,
          engagementReminderMinutes: item.engagementReminderMinutes,
          active: item.active,
        };
        const saved = draft.id
          ? await updateSpaOperator(draft.id, payload, token, tenant)
          : await createSpaOperator(payload, token, tenant);

        setCatalog((current) => ({
          ...current,
          operators: sortOperators(draft.id
            ? current.operators.map((entry) => (entry.id === draft.id ? {
              ...saved,
              qualifications: entry.qualifications || [],
              skills: operatorSkillLabels(saved, entry.qualifications || []),
            } : entry))
            : [...current.operators, saved]),
        }));
        setDraft(normalizeDraft("operators", saved));
        if (!draft.id) {
          await loadOperatorDetails(saved.id);
          showSnackbar(t("spa.catalog.operatorCreated", { defaultValue: "Operator created. You can now add qualifications and shifts." }), "success");
        } else {
          setDialogOpen(false);
          showSnackbar(t("spa.catalog.operatorSaved", { defaultValue: "Operator saved." }), "success");
        }
        return;
      }

      if (activeTab === "cabins") {
        const payload = {
          name: item.name,
          active: item.active,
          cabinType: item.cabinType,
          capacity: item.capacity,
          characteristics: item.characteristics,
        };
        const saved = draft.id
          ? await updateSpaCabin(draft.id, payload, token, tenant)
          : await createSpaCabin(payload, token, tenant);

        setCatalog((current) => ({
          ...current,
          cabins: draft.id
            ? current.cabins.map((entry) => (entry.id === draft.id ? { ...entry, ...saved, currentStatus: currentCabinStatus(saved) } : entry))
            : [...current.cabins, { ...saved, currentStatus: currentCabinStatus(saved) }],
        }));
        setDialogOpen(false);
        showSnackbar(t("spa.catalog.cabinSaved", { defaultValue: "Cabin saved." }), "success");
        return;
      }

      if (activeTab === "equipment") {
        if (!validEquipmentPlacements(item.quantity, item.placements)) throw new Error(t('spa.equipment.invalidPlacements'));
        const payload = {
          name: item.name,
          description: item.description?.trim() || null,
          quantity: item.quantity,
          active: item.active !== false,
          placements: (item.placements || []).map((placement) => ({ roomId: Number(placement.roomId), quantity: Number(placement.quantity) })),
        };
        const saved = draft.id
          ? await updateSpaEquipment(draft.id, payload, token, tenant)
          : await createSpaEquipment(payload, token, tenant);

        setCatalog((current) => ({
          ...current,
          equipment: draft.id
            ? current.equipment.map((entry) => (entry.id === draft.id ? saved : entry))
            : [...current.equipment, saved],
        }));
        setDialogOpen(false);
        return;
      }

      if (activeTab === "products") {
        const payload = {
          name: item.name.trim(),
          unit: item.unit,
          retailCategory: item.retailCategory || "OTHER",
          unitCost: item.unitCost,
          sellingPrice: item.sellingPrice,
        };
        const saved = item.id
          ? await updateSpaInventoryProduct(item.id, payload, token, tenant)
          : await createSpaInventoryProduct(payload, token, tenant);
        setCatalog((current) => ({
          ...current,
          products: (item.id
            ? current.products.map((entry) => (entry.id === saved.id ? saved : entry))
            : [...current.products, saved]
          ).sort((left, right) => String(left.name || "").localeCompare(String(right.name || ""))),
        }));
        setDialogOpen(false);
        showSnackbar(t("spa.products.saved"), "success");
      }
    } catch (error) {
      setSaveError(error?.message || t("spa.common.saveError", { defaultValue: "Save failed" }));
    } finally {
      saveInFlight.current = false;
      setSaving(false);
    }
  };

  const addOperatorQualification = async () => {
    if (!draft.id || !canManageResources) return;
    setSaveError("");
    try {
      const payload = operatorQualificationDraft.treatmentProductId
        ? { treatmentProductId: Number(operatorQualificationDraft.treatmentProductId) }
        : { skill: operatorQualificationDraft.skill };
      await addSpaOperatorQualification(draft.id, payload, token, tenant);
      setOperatorQualificationDraft({ treatmentProductId: "", skill: "" });
      await loadOperatorDetails(draft.id);
    } catch (error) {
      setSaveError(error?.message || t("spa.catalog.qualificationSaveError", { defaultValue: "Could not save qualification" }));
    }
  };

  const removeOperatorQualification = async (qualificationId) => {
    if (!draft.id || !canManageResources) return;
    setSaveError("");
    try {
      await removeSpaOperatorQualification(draft.id, qualificationId, token, tenant);
      await loadOperatorDetails(draft.id);
    } catch (error) {
      setSaveError(error?.message || t("spa.catalog.qualificationRemoveError", { defaultValue: "Could not remove qualification" }));
    }
  };

  const addOperatorScheduleEntry = async () => {
    if (!draft.id || !canManageResources) return;
    setSaveError("");
    try {
      await createSpaOperatorScheduleEntry(draft.id, {
        type: operatorScheduleDraft.type,
        dayOfWeek: Number(operatorScheduleDraft.dayOfWeek),
        startTime: operatorScheduleDraft.startTime,
        endTime: operatorScheduleDraft.endTime,
        ...(operatorScheduleDraft.note ? { note: operatorScheduleDraft.note } : {}),
      }, token, tenant);
      await loadOperatorDetails(draft.id);
    } catch (error) {
      setSaveError(error?.message || t("spa.catalog.scheduleSaveError", { defaultValue: "Could not save schedule" }));
    }
  };

  const removeOperatorScheduleEntry = async (entryId) => {
    if (!draft.id || !canManageResources) return;
    setSaveError("");
    try {
      await removeSpaOperatorScheduleEntry(draft.id, entryId, token, tenant);
      await loadOperatorDetails(draft.id);
    } catch (error) {
      setSaveError(error?.message || t("spa.catalog.scheduleRemoveError", { defaultValue: "Could not remove schedule entry" }));
    }
  };

  const deactivateOperator = async () => {
    if (!draft.id || !canManageResources) return;
    setSaveError("");
    try {
      const saved = await deactivateSpaOperator(draft.id, token, tenant);
      setCatalog((current) => ({
        ...current,
        operators: current.operators.map((entry) => (entry.id === draft.id ? {
          ...saved,
          qualifications: entry.qualifications || [],
          skills: operatorSkillLabels(saved, entry.qualifications || []),
        } : entry)),
      }));
      setDraft(normalizeDraft("operators", saved));
    } catch (error) {
      setSaveError(error?.message || t("spa.catalog.operatorDeactivateError", { defaultValue: "Could not deactivate operator" }));
    }
  };

  const deactivateCabin = async () => {
    if (!draft.id || !canManageResources) return;
    setSaveError("");
    try {
      const saved = await deactivateSpaCabin(draft.id, token, tenant);
      setCatalog((current) => ({
        ...current,
        cabins: current.cabins.map((entry) => (entry.id === draft.id ? { ...entry, ...saved, currentStatus: "OUT_OF_SERVICE" } : entry)),
      }));
      setDraft((current) => ({ ...current, ...normalizeDraft("cabins", saved), blockDate: current.blockDate || todayKey() }));
      showSnackbar(t("spa.catalog.cabinDeactivated", { defaultValue: "Cabin set out of service." }), "success");
    } catch (error) {
      setSaveError(error?.message || t("spa.catalog.cabinDeactivateError", { defaultValue: "Could not deactivate cabin" }));
    }
  };

  const deactivateEquipment = async () => {
    if (!draft.id || !canManageResources || saveInFlight.current || equipmentBusy || equipmentPending) return;
    saveInFlight.current = true; setSaving(true); setSaveError('');
    try {
      const saved = await deactivateSpaEquipment(draft.id, token, tenant);
      setCatalog((current) => ({ ...current, equipment: current.equipment.map((item) => item.id === saved.id ? saved : item) }));
      setDraft(normalizeDraft('equipment', saved));
      setConfirmEquipmentDeactivate(false);
      showSnackbar(t('spa.equipment.deactivated'), 'success');
    } catch (error) { setSaveError(error.message); }
    finally { saveInFlight.current = false; setSaving(false); }
  };

  const addCabinBlock = async () => {
    if (!draft.id || !canManageResources) return;
    setSaveError("");
    try {
      await createSpaCabinBlock(draft.id, {
        type: draft.blockType,
        startAt: dateTimeWithLocalOffset(draft.blockDate || todayKey(), draft.blockStartTime),
        endAt: dateTimeWithLocalOffset(draft.blockDate || todayKey(), draft.blockEndTime),
        ...(draft.blockNote ? { note: draft.blockNote } : {}),
      }, token, tenant);
      await loadCabinBlocks(draft.id);
      showSnackbar(t("spa.catalog.cabinBlockSaved", { defaultValue: "Cabin block saved." }), "success");
    } catch (error) {
      setSaveError(error?.message || t("spa.catalog.cabinBlockSaveError", { defaultValue: "Could not save cabin block" }));
    }
  };

  const removeCabinBlock = async (blockId) => {
    if (!draft.id || !canManageResources) return;
    setSaveError("");
    try {
      await removeSpaCabinBlock(draft.id, blockId, token, tenant);
      await loadCabinBlocks(draft.id);
    } catch (error) {
      setSaveError(error?.message || t("spa.catalog.cabinBlockRemoveError", { defaultValue: "Could not remove cabin block" }));
    }
  };

  const rows = catalog[activeTab] || [];

  if (tenantType !== "SPA") {
    return (
      <SidebarLayout className="admin-responsive-page">
        <Container sx={{ py: 4 }}>
          <Alert severity="warning">{t("spa.common.spaOnly")}</Alert>
        </Container>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout className="admin-responsive-page">
      <Container maxWidth={false} sx={{ py: { xs: 2, md: 3 }, px: { xs: 1.5, md: 4 }, maxWidth: 1680 }}>
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 2, ...pageSurface }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} spacing={2}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <SpaIcon sx={{ color: "#D47200" }} />
                <Typography variant="h5" fontWeight={900}>{t("spa.catalog.title")}</Typography>
              </Stack>
            </Box>
            {activeTab === "treatments" || activeTab === "operators" || activeTab === "cabins" || activeTab === "equipment" || activeTab === "products" ? (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openCreate}
                disabled={!canManageActiveTab}
                sx={{ minWidth: 180 }}
              >
                {t("spa.catalog.addItem", { item: activeTabSingular })}
              </Button>
            ) : null}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ mb: 2, ...pageSurface }}>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="scrollable"
            allowScrollButtonsMobile
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.key}
                value={tab.key}
                label={t(`spa.catalog.tabs.${tab.key}.label`, { defaultValue: tab.label })}
                icon={tab.icon}
                iconPosition="start"
                disabled={["equipment", "products"].includes(tab.key) ? !canViewResources : tab.key !== "treatments" && !(canViewResources || canViewFinance)}
              />
            ))}
          </Tabs>
        </Paper>

        {loading ? (
          <Alert severity="info">{t("spa.catalog.loading")}</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ ...pageSurface }}>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      fontWeight: 900,
                      backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "#F8FAFC",
                    },
                  }}
                >
                  {activeTab === "treatments" && (
                    <>
                      <TableCell>{t("spa.fields.name")}</TableCell>
                      <TableCell>{t("spa.fields.category")}</TableCell>
                      <TableCell align="right">{t("spa.fields.duration")}</TableCell>
                      <TableCell align="right">{t("spa.fields.price")}</TableCell>
                      <TableCell align="right">{t("spa.fields.vat")}</TableCell>
                      <TableCell>{t("spa.fields.status")}</TableCell>
                    </>
                  )}
                  {activeTab === "operators" && (
                    <>
                      <TableCell>{t("spa.fields.name")}</TableCell>
                      <TableCell>{t("spa.fields.skills")}</TableCell>
                      <TableCell>{t("spa.fields.email")}</TableCell>
                      <TableCell>{t("spa.fields.status")}</TableCell>
                    </>
                  )}
                  {activeTab === "cabins" && (
                    <>
                      <TableCell>{t("spa.fields.name")}</TableCell>
                      <TableCell>{t("spa.fields.type", { defaultValue: "Type" })}</TableCell>
                      <TableCell align="right">{t("spa.fields.capacity", { defaultValue: "Capacity" })}</TableCell>
                      <TableCell>{t("spa.catalog.compatibleTreatments", { defaultValue: "Compatible treatments" })}</TableCell>
                      <TableCell>{t("spa.catalog.equipment", { defaultValue: "Equipment" })}</TableCell>
                      <TableCell>{t("spa.fields.status")}</TableCell>
                    </>
                  )}
                  {activeTab === "equipment" && (
                    <>
                      <TableCell>{t("spa.fields.name")}</TableCell>
                      <TableCell>{t("spa.equipment.placements")}</TableCell>
                      <TableCell align="right">{t("spa.fields.quantity")}</TableCell>
                      <TableCell>{t("spa.fields.status")}</TableCell>
                    </>
                  )}
                  {activeTab === "products" && (
                    <>
                      <TableCell>{t("spa.fields.name")}</TableCell>
                      <TableCell>{t("spa.products.retailCategory")}</TableCell>
                      <TableCell>{t("spa.products.unit")}</TableCell>
                      <TableCell align="right">{t("spa.products.unitCost")}</TableCell>
                      <TableCell align="right">{t("spa.products.sellingPrice")}</TableCell>
                    </>
                  )}
                  <TableCell align="right">{t("spa.fields.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    {activeTab === "treatments" && (
                      <>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.category}</TableCell>
                        <TableCell align="right">{Number(row.durationMinutes) > 0 ? `${row.durationMinutes} min` : "-"}</TableCell>
                        <TableCell align="right">€ {Number(row.price || 0).toFixed(2)}</TableCell>
                        <TableCell align="right">{row.vatRate}%</TableCell>
                        <TableCell><StatusChip value={row.active} /></TableCell>
                      </>
                    )}
                    {activeTab === "operators" && (
                      <>
                        <TableCell>{row.displayName || [row.name, row.surname].filter(Boolean).join(" ") || row.name}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {(row.skills || []).map((skill) => <Chip key={skill} size="small" label={skill} />)}
                            {!(row.skills || []).length ? <Typography variant="caption" color="text.secondary">-</Typography> : null}
                          </Stack>
                        </TableCell>
                        <TableCell>{row.email || "-"}</TableCell>
                        <TableCell><StatusChip value={row.active} /></TableCell>
                      </>
                    )}
                    {activeTab === "cabins" && (
                      <>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.cabinType || "-"}</TableCell>
                        <TableCell align="right">{row.capacity}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {(row.compatibleTreatments || []).slice(0, 3).map((treatment) => (
                              <Chip key={treatment.id || treatment.name} size="small" label={treatment.name || treatment.id} />
                            ))}
                            {(row.compatibleTreatments || []).length > 3 ? <Chip size="small" label={`+${row.compatibleTreatments.length - 3}`} /> : null}
                            {!(row.compatibleTreatments || []).length ? <Typography variant="caption" color="text.secondary">-</Typography> : null}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {(row.equipment || []).slice(0, 3).map((item) => (
                              <Chip key={item.id || item.name} size="small" label={`${item.name || item.id}${item.quantity ? ` x${item.quantity}` : ""}`} />
                            ))}
                            {(row.equipment || []).length > 3 ? <Chip size="small" label={`+${row.equipment.length - 3}`} /> : null}
                            {!(row.equipment || []).length ? <Typography variant="caption" color="text.secondary">-</Typography> : null}
                          </Stack>
                        </TableCell>
                        <TableCell><StatusChip value={row.currentStatus || row.status} /></TableCell>
                      </>
                    )}
                    {activeTab === "equipment" && (
                      <>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{(row.placements || []).map((placement) => `${catalog.cabins.find((cabin) => Number(cabin.id) === Number(placement.roomId))?.name || `#${placement.roomId}`} x${placement.quantity}`).join(', ') || '-'}</TableCell>
                        <TableCell align="right">{row.quantity}</TableCell>
                        <TableCell><StatusChip value={row.active !== false} /></TableCell>
                      </>
                    )}
                    {activeTab === "products" && (
                      <>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{t(`spa.products.categories.${row.retailCategory || "OTHER"}`)}</TableCell>
                        <TableCell>{t(`spa.products.units.${row.unit}`, { defaultValue: row.unit })}</TableCell>
                        <TableCell align="right">EUR {Number(row.unitCost || 0).toFixed(2)}</TableCell>
                        <TableCell align="right">EUR {Number(row.sellingPrice || 0).toFixed(2)}</TableCell>
                      </>
                    )}
                    <TableCell align="right">
                      {activeTab === "treatments" || activeTab === "operators" || activeTab === "cabins" || activeTab === "equipment" || activeTab === "products" ? (
                        <IconButton size="small" onClick={() => openEdit(row)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <Typography variant="caption" sx={{ color: isDark ? "rgba(255,255,255,0.54)" : "text.secondary" }}>
                          {activeTab === "operators" ? t("spa.catalog.managedInSpaOperators", { defaultValue: "Managed in SPA operators" }) : t("spa.catalog.managedInSpaTreatments", { defaultValue: "Managed in SPA treatments" })}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {activeTab === 'equipment' && !rows.length && !equipmentError && <TableRow><TableCell colSpan={5}>{t('spa.equipment.empty')}</TableCell></TableRow>}
                {activeTab === 'products' && !rows.length && !inventoryProductsError && <TableRow><TableCell colSpan={6}>{t('spa.products.empty')}</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {activeTab === 'equipment' && equipmentError && <Alert severity="error" action={<Button onClick={() => setCatalogReload((value) => value + 1)}>{t('spa.equipment.retry')}</Button>}>{equipmentError}</Alert>}
        {activeTab === 'products' && inventoryProductsError && <Alert severity="error" action={<Button onClick={() => setCatalogReload((value) => value + 1)}>{t('spa.equipment.retry')}</Button>}>{inventoryProductsError}</Alert>}
        <Dialog open={dialogOpen} onClose={() => { if (!saving && !equipmentBusy && !treatmentAddOnSaving) setDialogOpen(false); }} fullWidth maxWidth="md">
          <DialogTitle>{draft.id ? t("spa.catalog.editItem", { item: activeTabSingular }) : t("spa.catalog.addItem", { item: activeTabSingular })}</DialogTitle>
          {saveError ? <Alert severity="error" sx={{ mx: 3, mb: 2, flexShrink: 0 }}>{saveError}</Alert> : null}
          <DialogContent dividers>
            {!canManageActiveTab && <Alert severity="info" sx={{ mb: 2 }}>{t("spa.common.readOnly")}</Alert>}
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid item xs={12}>
                <TextField disabled={!canManageActiveTab || Boolean(equipmentPending)}
                  fullWidth
                  label={t("spa.fields.name")}
                  value={draft.name}
                  onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
                />
              </Grid>

              {activeTab === "operators" && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField disabled={!canManageActiveTab}
                      fullWidth
                      label={t("spa.fields.surname", { defaultValue: "Surname" })}
                      value={draft.surname || ""}
                      onChange={(event) => setDraft((current) => ({ ...current, surname: event.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField disabled={!canManageActiveTab}
                      fullWidth
                      label={t("spa.fields.email")}
                      value={draft.email || ""}
                      onChange={(event) => setDraft((current) => ({ ...current, email: event.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField disabled={!canManageActiveTab}
                      fullWidth
                      label={t("spa.fields.phone", { defaultValue: "Phone" })}
                      value={draft.phone || ""}
                      onChange={(event) => setDraft((current) => ({ ...current, phone: event.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField disabled={!canManageActiveTab}
                      fullWidth
                      label={t("spa.fields.languages", { defaultValue: "Languages" })}
                      value={draft.languages || ""}
                      helperText={t("spa.catalog.commaSeparated", { defaultValue: "Comma separated" })}
                      onChange={(event) => setDraft((current) => ({ ...current, languages: event.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField disabled={!canManageActiveTab}
                      fullWidth
                      label={t("spa.fields.skills")}
                      value={draft.specialties || draft.skills || ""}
                      helperText={t("spa.catalog.commaSeparated", { defaultValue: "Comma separated" })}
                      onChange={(event) => setDraft((current) => ({ ...current, specialties: event.target.value, skills: event.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField disabled={!canManageActiveTab}
                      fullWidth
                      type="number"
                      label={t("spa.catalog.engagementReminderMinutes", { defaultValue: "Engagement reminder minutes" })}
                      value={draft.engagementReminderMinutes ?? 60}
                      inputProps={{ min: 5, max: 10080, step: 5 }}
                      helperText={t("spa.catalog.engagementReminderHelp", { defaultValue: "From 5 minutes to 7 days before the appointment." })}
                      onChange={(event) => setDraft((current) => ({ ...current, engagementReminderMinutes: event.target.value }))}
                    />
                  </Grid>
                  {draft.id ? (
                    <>
                      <Grid item xs={12}>
                        <Stack spacing={1}>
                          <Typography fontWeight={900}>{t("spa.catalog.qualifications", { defaultValue: "Qualifications" })}</Typography>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {operatorDetails.qualifications.length ? operatorDetails.qualifications.map((qualification) => (
                              <Chip disabled={!canManageActiveTab}
                                key={qualification.id}
                                label={qualification.type === "TREATMENT" ? qualification.treatment?.name : qualification.skill}
                                onDelete={canManageResources ? () => removeOperatorQualification(qualification.id) : undefined}
                              />
                            )) : (
                              <Chip disabled={!canManageActiveTab} size="small" color="warning" variant="outlined" label={t("spa.catalog.noQualifications", { defaultValue: "No qualifications" })} />
                            )}
                          </Stack>
                          <Grid container spacing={1}>
                            <Grid item xs={12} sm={5}>
                              <TextField disabled={!canManageActiveTab}
                                select
                                fullWidth
                                size="small"
                                label={t("spa.fields.treatment")}
                                value={operatorQualificationDraft.treatmentProductId}
                                onChange={(event) => setOperatorQualificationDraft({ treatmentProductId: event.target.value, skill: "" })}
                              >
                                <MenuItem value="">{t("all", { defaultValue: "None" })}</MenuItem>
                                {catalog.treatments.map((treatment) => (
                                  <MenuItem key={treatment.id} value={treatment.id}>{treatment.name}</MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                              <TextField disabled={!canManageActiveTab}
                                fullWidth
                                size="small"
                                label={t("spa.fields.skill", { defaultValue: "Skill" })}
                                value={operatorQualificationDraft.skill}
                                onChange={(event) => setOperatorQualificationDraft({ treatmentProductId: "", skill: event.target.value })}
                              />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                              <Button
                                fullWidth
                                variant="outlined"
                                onClick={addOperatorQualification}
                                disabled={!canManageActiveTab || (!canManageResources || (!operatorQualificationDraft.treatmentProductId && !operatorQualificationDraft.skill.trim()))}
                                sx={{ minHeight: 40 }}
                              >
                                {t("spa.common.add", { defaultValue: "Add" })}
                              </Button>
                            </Grid>
                          </Grid>
                        </Stack>
                      </Grid>

                      <Grid item xs={12}>
                        <Stack spacing={1}>
                          <Typography fontWeight={900}>{t("spa.catalog.weeklySchedule", { defaultValue: "Weekly schedule" })}</Typography>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {operatorDetails.weekly.length ? operatorDetails.weekly.map((entry) => (
                              <Chip disabled={!canManageActiveTab}
                                key={entry.id}
                                label={`${t(`spa.settings.days.${entry.dayOfWeek}`)} ${t(`spa.catalog.scheduleTypes.${entry.type.toLowerCase()}`)} ${entry.startTime}-${entry.endTime}`}
                                onDelete={canManageResources ? () => removeOperatorScheduleEntry(entry.id) : undefined}
                              />
                            )) : (
                              <Chip disabled={!canManageActiveTab} size="small" color="warning" variant="outlined" label={t("spa.catalog.noSchedule", { defaultValue: "No weekly shifts" })} />
                            )}
                          </Stack>
                          <Grid container spacing={1}>
                            <Grid item xs={12} sm={2}>
                              <TextField disabled={!canManageActiveTab}
                                select
                                fullWidth
                                size="small"
                                label={t("spa.fields.type")}
                                value={operatorScheduleDraft.type}
                                onChange={(event) => setOperatorScheduleDraft((current) => ({ ...current, type: event.target.value }))}
                              >
                                <MenuItem value="SHIFT">{t("spa.catalog.scheduleTypes.shift", { defaultValue: "Shift" })}</MenuItem>
                                <MenuItem value="BREAK">{t("spa.catalog.scheduleTypes.break", { defaultValue: "Break" })}</MenuItem>
                              </TextField>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <TextField disabled={!canManageActiveTab}
                                select
                                fullWidth
                                size="small"
                                label={t("spa.settings.day")}
                                value={operatorScheduleDraft.dayOfWeek}
                                onChange={(event) => setOperatorScheduleDraft((current) => ({ ...current, dayOfWeek: Number(event.target.value) }))}
                              >
                                {[0, 1, 2, 3, 4, 5, 6].map((index) => (
                                  <MenuItem key={index} value={index}>{t(`spa.settings.days.${index}`)}</MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            <Grid item xs={6} sm={2}>
                              <TextField disabled={!canManageActiveTab}
                                fullWidth
                                size="small"
                                type="time"
                                label={t("spa.settings.start")}
                                value={operatorScheduleDraft.startTime}
                                onChange={(event) => setOperatorScheduleDraft((current) => ({ ...current, startTime: event.target.value }))}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={6} sm={2}>
                              <TextField disabled={!canManageActiveTab}
                                fullWidth
                                size="small"
                                type="time"
                                label={t("spa.settings.end")}
                                value={operatorScheduleDraft.endTime}
                                onChange={(event) => setOperatorScheduleDraft((current) => ({ ...current, endTime: event.target.value }))}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <Button
                                fullWidth
                                variant="outlined"
                                onClick={addOperatorScheduleEntry}
                                disabled={!canManageActiveTab || (!canManageResources || !operatorScheduleDraft.startTime || !operatorScheduleDraft.endTime || operatorScheduleDraft.startTime === operatorScheduleDraft.endTime)}
                                sx={{ minHeight: 40 }}
                              >
                                {t("spa.common.add", { defaultValue: "Add" })}
                              </Button>
                            </Grid>
                          </Grid>
                        </Stack>
                      </Grid>

                    </>
                  ) : (
                    <Grid item xs={12}>
                      <Alert severity="info">
                        {t("spa.catalog.saveOperatorFirst", { defaultValue: "Save the operator first, then add qualifications and weekly shifts." })}
                      </Alert>
                    </Grid>
                  )}
                </>
              )}

              {activeTab === "treatments" && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField disabled={!canManageActiveTab}
                      select
                      fullWidth
                      label={t("spa.fields.category")}
                      value={draft.categoryId || ""}
                      onChange={(event) => {
                        const categoryId = event.target.value;
                        const selectedCategory = categories.find((category) => Number(category.id) === Number(categoryId));
                        setDraft((current) => ({
                          ...current,
                          categoryId,
                          subcategoryId: "",
                          vatRate: selectedCategory?.vatRate ?? current.vatRate,
                        }));
                      }}
                    >
                      <MenuItem value="">{t("all", { defaultValue: "None" })}</MenuItem>
                      {categories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label={t("subcategory", { defaultValue: "Subcategory" })}
                      value={draft.subcategoryId || ""}
                      disabled={!canManageActiveTab || (!draft.categoryId)}
                      onChange={(event) => setDraft((current) => ({ ...current, subcategoryId: event.target.value }))}
                    >
                      <MenuItem value="">{t("all", { defaultValue: "None" })}</MenuItem>
                      {(categories.find((category) => Number(category.id) === Number(draft.categoryId))?.subcategories || []).map((subcategory) => (
                        <MenuItem key={subcategory.id} value={subcategory.id}>{subcategory.name}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField disabled={!canManageActiveTab} fullWidth type="number" label={t("spa.fields.durationMinutes")} value={draft.durationMinutes} onChange={(event) => setDraft((current) => ({ ...current, durationMinutes: event.target.value }))} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField disabled={!canManageActiveTab} fullWidth type="number" label={t("spa.fields.preparationMinutes", { defaultValue: "Preparation minutes" })} value={draft.preparationMinutes} onChange={(event) => setDraft((current) => ({ ...current, preparationMinutes: event.target.value }))} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField disabled={!canManageActiveTab} fullWidth type="number" label={t("spa.fields.cleanupMinutes", { defaultValue: "Cleanup minutes" })} value={draft.cleanupMinutes} onChange={(event) => setDraft((current) => ({ ...current, cleanupMinutes: event.target.value }))} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField disabled={!canManageActiveTab} fullWidth type="number" label={t("spa.fields.price")} value={draft.price} onChange={(event) => setDraft((current) => ({ ...current, price: event.target.value }))} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField disabled={!canManageActiveTab} fullWidth type="number" label={t("spa.fields.vatPercent")} value={draft.vatRate} onChange={(event) => setDraft((current) => ({ ...current, vatRate: event.target.value }))} />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 0.5 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Stack spacing={1.5}>
                      <Box>
                        <Typography fontWeight={900}>{t("spa.catalog.addOnsTitle", { defaultValue: "Add-ons and upgrades" })}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("spa.catalog.addOnsHelp", { defaultValue: "Configure optional services, upgrades and products for this treatment." })}
                        </Typography>
                      </Box>
                      {!draft.id ? (
                        <Alert severity="info">
                          {t("spa.catalog.saveTreatmentFirst", { defaultValue: "Save the treatment first, then configure its add-ons." })}
                        </Alert>
                      ) : (
                        <>
                          {treatmentAddOnsLoading ? <CircularProgress size={24} /> : null}
                          {!treatmentAddOnsLoading && !treatmentAddOns.length ? (
                            <Alert severity="info">{t("spa.catalog.noAddOns", { defaultValue: "No add-ons configured for this treatment." })}</Alert>
                          ) : null}
                          {treatmentAddOns.map((addOn) => (
                            <Paper key={addOn.id} variant="outlined" sx={{ p: 1.25 }}>
                              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Typography fontWeight={800}>{addOn.name || addOn.product?.name || `Add-on ${addOn.id}`}</Typography>
                                    <Chip size="small" variant="outlined" label={t(`spa.addOnTypes.${addOn.type}`, { defaultValue: addOn.type })} />
                                    <StatusChip value={addOn.active} />
                                  </Stack>
                                  <Typography variant="body2" color="text.secondary">
                                    +EUR {Number(addOn.priceDelta ?? addOn.priceAdjustment ?? 0).toFixed(2)} | +{Number(addOn.durationDeltaMinutes ?? addOn.durationAdjustmentMinutes ?? 0)} min
                                  </Typography>
                                </Box>
                                <IconButton
                                  size="small"
                                  aria-label={t("spa.common.edit", { defaultValue: "Edit" })}
                                  onClick={() => {
                                    setTreatmentAddOnDraft(normalizeTreatmentAddOnDraft(addOn));
                                    setEquipmentRequirementDraft({ equipmentId: "", quantity: 1 });
                                  }}
                                  disabled={treatmentAddOnSaving || !canManageCatalog}
                                >
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                                {addOn.active !== false ? (
                                  <IconButton
                                    size="small"
                                    color="error"
                                    aria-label={t("spa.catalog.deactivate", { defaultValue: "Deactivate" })}
                                    onClick={() => deactivateTreatmentAddOn(addOn.id)}
                                    disabled={treatmentAddOnSaving || !canManageCatalog}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                ) : null}
                              </Stack>
                            </Paper>
                          ))}

                          <Paper variant="outlined" sx={{ p: 1.5 }}>
                            <Grid container spacing={1.5}>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  select
                                  fullWidth
                                  size="small"
                                  label={t("spa.fields.type", { defaultValue: "Type" })}
                                  value={treatmentAddOnDraft.type}
                                  onChange={(event) => setTreatmentAddOnDraft((current) => ({
                                    ...current,
                                    type: event.target.value,
                                    productId: current.id ? current.productId : "",
                                  }))}
                                  disabled={addOnReadOnly}
                                >
                                  {treatmentAddOnTypes.map((type) => (
                                    <MenuItem key={type} value={type}>{t(`spa.addOnTypes.${type}`, { defaultValue: type })}</MenuItem>
                                  ))}
                                </TextField>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  select
                                  fullWidth
                                  size="small"
                                  label={t("spa.catalog.addOnProduct", { defaultValue: "Service or product" })}
                                  value={treatmentAddOnDraft.productId}
                                  onChange={(event) => setTreatmentAddOnDraft((current) => ({ ...current, productId: event.target.value }))}
                                  disabled={Boolean(treatmentAddOnDraft.id) || addOnReadOnly}
                                >
                                  {addOnProductOptions.map((product) => (
                                    <MenuItem key={product.id} value={String(product.id)}>
                                      {product.name} | EUR {Number(product.price || 0).toFixed(2)}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="number"
                                  label={t("spa.catalog.priceAdjustment", { defaultValue: "Price adjustment" })}
                                  value={treatmentAddOnDraft.priceDelta}
                                  disabled={addOnReadOnly}
                                  inputProps={{ min: 0, step: 0.01 }}
                                  onChange={(event) => setTreatmentAddOnDraft((current) => ({ ...current, priceDelta: event.target.value }))}
                                />
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="number"
                                  label={t("spa.catalog.durationAdjustment", { defaultValue: "Extra minutes" })}
                                  value={treatmentAddOnDraft.durationDeltaMinutes}
                                  disabled={addOnReadOnly}
                                  inputProps={{ min: 0, step: 1 }}
                                  onChange={(event) => setTreatmentAddOnDraft((current) => ({ ...current, durationDeltaMinutes: event.target.value }))}
                                />
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="number"
                                  label={t("spa.catalog.requiredOperators", { defaultValue: "Required operators" })}
                                  value={treatmentAddOnDraft.operatorCount}
                                  disabled={addOnReadOnly}
                                  inputProps={{ min: 1, step: 1 }}
                                  onChange={(event) => setTreatmentAddOnDraft((current) => ({ ...current, operatorCount: event.target.value }))}
                                />
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <TextField
                                  select
                                  fullWidth
                                  size="small"
                                  label={t("spa.fields.status")}
                                  value={treatmentAddOnDraft.active === false ? "INACTIVE" : "ACTIVE"}
                                  disabled={addOnReadOnly}
                                  onChange={(event) => setTreatmentAddOnDraft((current) => ({ ...current, active: event.target.value === "ACTIVE" }))}
                                >
                                  <MenuItem value="ACTIVE">{t("spa.catalog.statuses.active")}</MenuItem>
                                  <MenuItem value="INACTIVE">{t("spa.catalog.statuses.inactive")}</MenuItem>
                                </TextField>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  select
                                  fullWidth
                                  size="small"
                                  label={t("spa.catalog.compatibleCabins", { defaultValue: "Compatible cabins" })}
                                  value={treatmentAddOnDraft.compatibleCabinIds}
                                  disabled={addOnReadOnly}
                                  onChange={(event) => setTreatmentAddOnDraft((current) => ({ ...current, compatibleCabinIds: event.target.value }))}
                                  SelectProps={{
                                    multiple: true,
                                    renderValue: (selected) => selected.map((id) => catalog.cabins.find((cabin) => String(cabin.id) === String(id))?.name || id).join(", "),
                                  }}
                                >
                                  {catalog.cabins.map((cabin) => (
                                    <MenuItem key={cabin.id} value={String(cabin.id)}>
                                      <Checkbox checked={treatmentAddOnDraft.compatibleCabinIds.includes(String(cabin.id))} />
                                      <ListItemText primary={cabin.name} />
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label={t("spa.catalog.requiredSkills", { defaultValue: "Required operator skills" })}
                                  value={treatmentAddOnDraft.requiredOperatorSkills}
                                  disabled={addOnReadOnly}
                                  helperText={t("spa.catalog.commaSeparated", { defaultValue: "Comma separated" })}
                                  onChange={(event) => setTreatmentAddOnDraft((current) => ({ ...current, requiredOperatorSkills: event.target.value }))}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="body2" fontWeight={800}>{t("spa.catalog.requiredEquipment", { defaultValue: "Required equipment" })}</Typography>
                              </Grid>
                              <Grid item xs={12} sm={7}>
                                <TextField
                                  select
                                  fullWidth
                                  size="small"
                                  label={t("spa.catalog.equipment", { defaultValue: "Equipment" })}
                                  value={equipmentRequirementDraft.equipmentId}
                                  disabled={addOnReadOnly}
                                  onChange={(event) => setEquipmentRequirementDraft((current) => ({ ...current, equipmentId: event.target.value }))}
                                >
                                  {catalog.equipment.map((item) => <MenuItem key={item.id} value={String(item.id)}>{item.name}</MenuItem>)}
                                </TextField>
                              </Grid>
                              <Grid item xs={6} sm={2}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="number"
                                  label={t("spa.fields.quantity")}
                                  value={equipmentRequirementDraft.quantity}
                                  disabled={addOnReadOnly}
                                  inputProps={{ min: 1, step: 1 }}
                                  onChange={(event) => setEquipmentRequirementDraft((current) => ({ ...current, quantity: event.target.value }))}
                                />
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  onClick={addEquipmentRequirement}
                                  disabled={addOnReadOnly || !equipmentRequirementDraft.equipmentId}
                                  sx={{ minHeight: 40 }}
                                >
                                  {t("spa.common.add", { defaultValue: "Add" })}
                                </Button>
                              </Grid>
                              {(treatmentAddOnDraft.equipment || []).length ? (
                                <Grid item xs={12}>
                                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                    {treatmentAddOnDraft.equipment.map((requirement) => {
                                      const item = catalog.equipment.find((entry) => Number(entry.id) === Number(requirement.equipmentId));
                                      return (
                                        <Chip
                                          key={requirement.equipmentId}
                                          label={`${item?.name || requirement.equipmentId} x${requirement.quantity}`}
                                          onDelete={addOnReadOnly ? undefined : () => removeEquipmentRequirement(requirement.equipmentId)}
                                        />
                                      );
                                    })}
                                  </Stack>
                                </Grid>
                              ) : null}
                              <Grid item xs={12}>
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                  {treatmentAddOnDraft.id ? (
                                    <Button onClick={() => setTreatmentAddOnDraft(emptyTreatmentAddOnDraft())} disabled={treatmentAddOnSaving}>
                                      {t("spa.common.cancel")}
                                    </Button>
                                  ) : null}
                                  <Button
                                    variant="outlined"
                                    startIcon={treatmentAddOnSaving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                                    onClick={saveTreatmentAddOn}
                                    disabled={addOnReadOnly || !treatmentAddOnDraft.productId}
                                  >
                                    {treatmentAddOnDraft.id
                                      ? t("spa.catalog.updateAddOn", { defaultValue: "Update add-on" })
                                      : t("spa.catalog.addAddOn", { defaultValue: "Add add-on" })}
                                  </Button>
                                </Stack>
                              </Grid>
                            </Grid>
                          </Paper>
                        </>
                      )}
                    </Stack>
                  </Grid>
                </>
              )}

              {activeTab === "cabins" && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField disabled={!canManageActiveTab}
                      fullWidth
                      label={t("spa.fields.type", { defaultValue: "Type" })}
                      value={draft.cabinType || ""}
                      onChange={(event) => setDraft((current) => ({ ...current, cabinType: event.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField disabled={!canManageActiveTab}
                      fullWidth
                      type="number"
                      label={t("spa.fields.capacity", { defaultValue: "Capacity" })}
                      value={draft.capacity || 1}
                      inputProps={{ min: 1 }}
                      onChange={(event) => setDraft((current) => ({ ...current, capacity: event.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField disabled={!canManageActiveTab}
                      fullWidth
                      label={t("spa.catalog.characteristics", { defaultValue: "Characteristics" })}
                      value={draft.characteristics || ""}
                      helperText={t("spa.catalog.commaSeparated", { defaultValue: "Comma separated" })}
                      onChange={(event) => setDraft((current) => ({ ...current, characteristics: event.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField disabled={!canManageActiveTab}
                      select
                      fullWidth
                      label={t("spa.fields.status")}
                      value={draft.active === false ? "OUT_OF_SERVICE" : "AVAILABLE"}
                      onChange={(event) => setDraft((current) => ({ ...current, active: event.target.value !== "OUT_OF_SERVICE" }))}
                    >
                      <MenuItem value="AVAILABLE">{t("spa.catalog.statuses.available", { defaultValue: "Available" })}</MenuItem>
                      <MenuItem value="OUT_OF_SERVICE">{t("spa.catalog.statuses.out_of_service", { defaultValue: "Out of service" })}</MenuItem>
                    </TextField>
                  </Grid>

                  {draft.id ? (
                    <>
                      <Grid item xs={12}>
                        <Stack spacing={1}>
                          <Typography fontWeight={900}>{t("spa.catalog.cabinBlocks", { defaultValue: "Cabin status blocks" })}</Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={12} sm={3}>
                              <TextField disabled={!canManageActiveTab}
                                select
                                fullWidth
                                size="small"
                                label={t("spa.fields.status")}
                                value={draft.blockType || "MAINTENANCE"}
                                onChange={(event) => setDraft((current) => ({ ...current, blockType: event.target.value }))}
                              >
                                {cabinBlockTypeOptions.map((type) => (
                                  <MenuItem key={type} value={type}>{t(`spa.catalog.statuses.${type.toLowerCase()}`, { defaultValue: type.replace(/_/g, " ") })}</MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <TextField disabled={!canManageActiveTab}
                                fullWidth
                                size="small"
                                type="date"
                                label={t("spa.fields.date", { defaultValue: "Date" })}
                                value={draft.blockDate || todayKey()}
                                InputLabelProps={{ shrink: true }}
                                onChange={(event) => setDraft((current) => ({ ...current, blockDate: event.target.value }))}
                              />
                            </Grid>
                            <Grid item xs={6} sm={2}>
                              <TextField disabled={!canManageActiveTab}
                                fullWidth
                                size="small"
                                type="time"
                                label={t("spa.settings.start", { defaultValue: "Start" })}
                                value={draft.blockStartTime || "09:00"}
                                InputLabelProps={{ shrink: true }}
                                onChange={(event) => setDraft((current) => ({ ...current, blockStartTime: event.target.value }))}
                              />
                            </Grid>
                            <Grid item xs={6} sm={2}>
                              <TextField disabled={!canManageActiveTab}
                                fullWidth
                                size="small"
                                type="time"
                                label={t("spa.settings.end", { defaultValue: "End" })}
                                value={draft.blockEndTime || "18:00"}
                                InputLabelProps={{ shrink: true }}
                                onChange={(event) => setDraft((current) => ({ ...current, blockEndTime: event.target.value }))}
                              />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                              <Button
                                fullWidth
                                variant="outlined"
                                onClick={addCabinBlock}
                                disabled={!canManageActiveTab || (!canManageResources || !draft.blockStartTime || !draft.blockEndTime || draft.blockStartTime === draft.blockEndTime)}
                                sx={{ minHeight: 40 }}
                              >
                                {t("spa.common.add", { defaultValue: "Add" })}
                              </Button>
                            </Grid>
                            <Grid item xs={12}>
                              <TextField disabled={!canManageActiveTab}
                                fullWidth
                                size="small"
                                label={t("spa.fields.notes", { defaultValue: "Notes" })}
                                value={draft.blockNote || ""}
                                onChange={(event) => setDraft((current) => ({ ...current, blockNote: event.target.value }))}
                              />
                            </Grid>
                          </Grid>
                          <Stack spacing={0.75}>
                            {cabinBlocks.length ? cabinBlocks.map((block) => (
                              <Paper key={block.id} variant="outlined" sx={{ p: 1, bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#fff" }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <StatusChip value={block.type} />
                                  <Typography variant="body2" sx={{ flex: 1 }}>
                                    {new Date(block.startAt).toLocaleString()} - {new Date(block.endAt).toLocaleString()}
                                    {block.note ? ` | ${block.note}` : ""}
                                  </Typography>
                                  <IconButton size="small" color="error" onClick={() => removeCabinBlock(block.id)} disabled={!canManageActiveTab || (!canManageResources)}>
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </Paper>
                            )) : (
                              <Alert severity="info">{t("spa.catalog.noCabinBlocks", { defaultValue: "No cabin blocks for today." })}</Alert>
                            )}
                          </Stack>
                        </Stack>
                      </Grid>
                    </>
                  ) : (
                    <Grid item xs={12}>
                      <Alert severity="info">
                        {t("spa.catalog.saveCabinFirst", { defaultValue: "Save the cabin first, then add cleaning, maintenance or out-of-service blocks." })}
                      </Alert>
                    </Grid>
                  )}
                </>
              )}

              {activeTab === "products" && (
                <>
                  <Grid item xs={12} md={3}>
                    <TextField
                      select
                      fullWidth
                      label={t("spa.products.unit")}
                      value={draft.unit || "PIECE"}
                      disabled={!canManageResources || saving}
                      onChange={(event) => setDraft((current) => ({ ...current, unit: event.target.value }))}
                    >
                      {["PIECE", "ML", "GRAM"].map((unit) => (
                        <MenuItem key={unit} value={unit}>{t(`spa.products.units.${unit}`)}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      select
                      fullWidth
                      label={t("spa.products.retailCategory")}
                      value={draft.retailCategory || "OTHER"}
                      disabled={!canManageResources || saving}
                      onChange={(event) => setDraft((current) => ({ ...current, retailCategory: event.target.value }))}
                    >
                      {["COSMETICS", "WELLNESS", "TREATMENT_PRODUCT", "OTHER"].map((category) => (
                        <MenuItem key={category} value={category}>{t(`spa.products.categories.${category}`)}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t("spa.products.unitCost")}
                      value={draft.unitCost ?? 0}
                      inputProps={{ min: 0, max: 1000000, step: 0.01 }}
                      disabled={!canManageResources || saving}
                      onChange={(event) => setDraft((current) => ({ ...current, unitCost: event.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t("spa.products.sellingPrice")}
                      value={draft.sellingPrice ?? 0}
                      inputProps={{ min: 0, max: 1000000, step: 0.01 }}
                      disabled={!canManageResources || saving}
                      onChange={(event) => setDraft((current) => ({ ...current, sellingPrice: event.target.value }))}
                    />
                  </Grid>
                </>
              )}

              {activeTab === 'treatments' && <Grid item xs={12}>
                {draft.id ? <SpaTreatmentEquipment key={`${tenant}:${draft.id}`} treatmentId={draft.id} equipment={catalog.equipment} cabins={catalog.cabins}
                  inventoryProducts={catalog.products} equipmentLoaded={equipmentLoaded} inventoryProductsLoaded={inventoryProductsLoaded} inventoryProductsError={inventoryProductsError}
                  canViewEquipment={canViewResources} canManage={canManageCatalog && canViewResources && !saving} token={token} tenant={tenant} onBusyChange={setEquipmentBusy} />
                  : <Alert severity="info">{t('spa.equipment.saveTreatmentFirst')}</Alert>}
              </Grid>}
              {activeTab === 'equipment' && <Grid item xs={12}><Stack spacing={3}>
                {equipmentPending && (equipmentDetail.error
                  ? <Alert severity="error" action={<Button onClick={() => setEquipmentDetailReload((value) => value + 1)}>{t('spa.equipment.retry')}</Button>}>{equipmentDetail.error}</Alert>
                  : <CircularProgress size={22} aria-label={t('spa.common.loading')} />)}
                <SpaEquipmentFields draft={draft} setDraft={setDraft} cabins={catalog.cabins} disabled={!canManageResources || saving || equipmentBusy || Boolean(equipmentPending)} />
                {draft.id && equipmentDetail.ready && <SpaEquipmentBlocks key={`${tenant}:${draft.id}`} equipmentId={draft.id}
                  quantity={catalog.equipment.find((item) => item.id === draft.id)?.quantity || 1} canManage={canManageResources && !saving}
                  token={token} tenant={tenant} onBusyChange={setEquipmentBusy} />}
              </Stack></Grid>}
            </Grid>
          </DialogContent>
          <DialogActions>
            {activeTab === 'equipment' && draft.id && draft.active !== false && <Button color="error" startIcon={<DeleteOutlineIcon />}
              disabled={saving || equipmentBusy || Boolean(equipmentPending) || !canManageResources}
              onClick={() => { setSaveError(''); setConfirmEquipmentDeactivate(true); }}>{t('spa.catalog.deactivate', { defaultValue: 'Deactivate' })}</Button>}
            {activeTab === "operators" && draft.id && draft.active !== false ? (
              <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={deactivateOperator} disabled={saving || !canManageResources}>
                {t("spa.catalog.deactivate", { defaultValue: "Deactivate" })}
              </Button>
            ) : null}
            {activeTab === "cabins" && draft.id && draft.active !== false ? (
              <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={deactivateCabin} disabled={saving || !canManageResources}>
                {t("spa.catalog.deactivate", { defaultValue: "Deactivate" })}
              </Button>
            ) : null}
            <Box sx={{ flex: 1 }} />
            <Button disabled={saving || equipmentBusy || treatmentAddOnSaving} onClick={() => setDialogOpen(false)}>{t("spa.common.cancel")}</Button>
            <Button
              variant="contained"
              disabled={
                saving || equipmentBusy || treatmentAddOnSaving || Boolean(equipmentPending) || !canManageActiveTab || !String(draft.name || "").trim()
                || (activeTab === 'equipment' && !validEquipmentPlacements(draft.quantity, draft.placements))
                || (activeTab === "operators" && (!canManageResources || !String(draft.surname || "").trim()))
                || (activeTab === "cabins" && (!canManageResources || !String(draft.cabinType || "").trim() || Number(draft.capacity || 0) <= 0))
                || (activeTab === "treatments" && Number(draft.durationMinutes || 0) <= 0)
                || (activeTab === "products" && (Number(draft.unitCost) < 0 || Number(draft.sellingPrice) < 0))
              }
              onClick={saveDraft}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {t("spa.common.save")}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={confirmEquipmentDeactivate && dialogOpen} onClose={() => { if (!saving) setConfirmEquipmentDeactivate(false); }}>
          <DialogTitle>{t('spa.equipment.confirmDeactivate')}</DialogTitle>
          {saveError && <Alert severity="error" sx={{ mx: 2 }}>{saveError}</Alert>}
          <DialogActions>
            <Button disabled={saving} onClick={() => setConfirmEquipmentDeactivate(false)}>{t('spa.common.cancel')}</Button>
            <Button color="error" disabled={saving || !canManageResources || Boolean(equipmentPending)} onClick={deactivateEquipment}>{t('spa.common.confirm')}</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </SidebarLayout>
  );
}

export default SpaCatalogPage;
